import {
  Button,
  Checkbox,
  DataTable,
  DataTableColumnHeader,
  DataTableSkeleton,
  DebouncedInput,
  useDataTable,
} from '@data-delivery/d3s';
import type { PermissionApi } from '@data-delivery/permission-ui-api';
import { useAssignUsersToAssignmentMutation } from '@data-delivery/permission-ui-data-access-state';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { showErrorToast } from '../../utils/error-utils';
import { AssignmentUsersCsvDropzone } from './assignment-users-csv-dropzone';

export interface AssignUsersListProps {
  users: PermissionApi.UserEntity[];
  isLoading: boolean;
  selected: Record<string, boolean>;
  onToggle: (userId: string, checked: boolean) => void;
  className?: string;
  assignmentId?: string;
  onAssigned?: () => void;
}

export function AssignUsersList({
  users,
  isLoading,
  selected,
  onToggle,
  className,
  assignmentId,
  onAssigned,
}: AssignUsersListProps) {
  const [globalSearch, setGlobalSearch] = useState('');
  const mutation = useAssignUsersToAssignmentMutation();
  const rows = useMemo(() => {
    const copy = [...users];

    copy.sort((a, b) => {
      const aSelected = Boolean(
        selected[a.id] ??
          (assignmentId && Array.isArray(a.assignments)
            ? a.assignments.includes(assignmentId)
            : false),
      );
      const bSelected = Boolean(
        selected[b.id] ??
          (assignmentId && Array.isArray(b.assignments)
            ? b.assignments.includes(assignmentId)
            : false),
      );

      if (aSelected !== bSelected) {
        return bSelected ? 1 : -1;
      }

      return 0;
    });

    return copy;
  }, [users, assignmentId, selected]);
  type UserRow = (typeof rows)[number];

  const columns = useMemo<Array<ColumnDef<UserRow>>>(
    () => [
      {
        id: 'select',
        header: ({ column }) => <DataTableColumnHeader column={column} title='' />,
        cell: ({ row }) => (
          <Checkbox
            checked={Boolean(
              selected[row.original.id] ??
                (assignmentId && Array.isArray(row.original.assignments)
                  ? row.original.assignments.includes(assignmentId)
                  : false),
            )}
            onCheckedChange={c => {
              onToggle(row.original.id, Boolean(c));
            }}
            aria-label={`Select ${row.original.loginId}`}
          />
        ),
        enableSorting: false,
        size: 40,
      },
      {
        accessorKey: 'loginId',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Login ID' />,
        cell: ({ getValue }) => <span className='font-medium'>{getValue<string>()}</span>,
      },
      {
        id: 'name',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Name' />,
        accessorFn: r => `${(r.firstName || '').trim()} ${(r.lastName || '').trim()}`.trim(),
        cell: ({ getValue }) => <span>{(getValue<string>() || '—').trim() || '—'}</span>,
        enableSorting: false,
      },
      {
        accessorKey: 'orgName',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Org' />,
        cell: ({ getValue }) => <span>{getValue<string | undefined>() || '—'}</span>,
        enableSorting: false,
      },
      {
        id: 'lastModified',
        accessorFn: r => r.lastModified ?? 0,
        header: ({ column }) => <DataTableColumnHeader column={column} title='Last Modified' />,
        cell: ({ row }) => (
          <span>
            {row.original.lastModified ? new Date(row.original.lastModified).toLocaleString() : '—'}
          </span>
        ),
        sortingFn: 'basic',
        enableSorting: true,
      },
    ],
    [onToggle, selected, assignmentId],
  );

  const table = useDataTable<UserRow>({
    data: rows,
    columns,
    getRowId: r => r.id,
    externalState: {
      globalFilter: {
        state: globalSearch,
        setState: setGlobalSearch,
      },
    },
  });

  const onAssign = async () => {
    if (!assignmentId) {
      toast.error('Missing assignment ID');
      return;
    }

    const initiallyAssignedIds = users
      .filter(u => Array.isArray(u.assignments) && u.assignments.includes(assignmentId))
      .map(u => u.id);

    const effectiveSelectedIds = users
      .filter(u => {
        const toggle = selected[u.id];
        if (toggle === undefined) {
          return Array.isArray(u.assignments) && u.assignments.includes(assignmentId);
        }

        return Boolean(toggle);
      })
      .map(u => u.id);

    const removeUsers = initiallyAssignedIds.filter(id => !effectiveSelectedIds.includes(id));
    const addUsers = effectiveSelectedIds.filter(id => !initiallyAssignedIds.includes(id));

    if (addUsers.length === 0 && removeUsers.length === 0) {
      toast.error('No changes to apply');
      return;
    }

    try {
      await mutation.mutateAsync({ assignmentId, addUsers, removeUsers });
      toast.success('Users assignment updated');
      if (onAssigned) {
        onAssigned();
      }
    } catch (err) {
      showErrorToast(err);
    }
  };

  return (
    <div className={className}>
      {isLoading ? (
        <DataTableSkeleton
          withPagination={false}
          rowCount={10}
          columnCount={columns.length}
          searchableColumnCount={1}
          filterableColumnCount={0}
          showViewOptions={false}
          cellWidths={['3rem', '14rem', '16rem', '12rem', '12rem']}
          shrinkZero
        />
      ) : (
        <div className='flex flex-col gap-3'>
          <AssignmentUsersCsvDropzone
            users={users}
            assignmentId={assignmentId}
            selected={selected}
            isEditMode={Boolean(assignmentId)}
            onBulkSelectUsers={userIds => {
              userIds.forEach(userId => {
                onToggle(userId, true);
              });
            }}
            onBulkDeselectUsers={userIds => {
              userIds.forEach(userId => {
                onToggle(userId, false);
              });
            }}
          />
          <div className='flex items-center justify-between gap-2'>
            <DebouncedInput
              type='text'
              value={globalSearch}
              onChange={e => {
                setGlobalSearch(e.toString());
              }}
              placeholder='Search users…'
              className='h-9 flex-1 w-full min-w-0 rounded-md border bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            />
            <Button
              variant='outline'
              onClick={() => {
                setGlobalSearch('');
              }}
            >
              Reset
            </Button>
            <Button onClick={onAssign} disabled={!assignmentId || mutation.isPending}>
              Assign
            </Button>
          </div>
          <div className='flex flex-col rounded-md border'>
            <DataTable table={table} className='h-[calc(100vh-300px)]' />
          </div>
        </div>
      )}
    </div>
  );
}

export default AssignUsersList;
